import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useContent } from "@/content/ContentContext";

const LINKS = [
  { href: "#narrative", testId: "nav-link-narrative" },
  { href: "#ecentra-run", testId: "nav-link-ecentra" },
  { href: "#pillars", testId: "nav-link-pillars" },
  { href: "#wellness-journey", testId: "nav-link-wellness" },
  { href: "#transformation", testId: "nav-link-transformation" },
];

export const scrollToSection = (href) => {
  const el = document.querySelector(href);
  if (!el) return;
  if (window.__lenis) {
    window.__lenis.scrollTo(el, { offset: -72, duration: 1.5 });
  } else {
    el.scrollIntoView({ behavior: "smooth" });
  }
};

export default function Navbar() {
  const { content } = useContent();
  const nav = content.nav;
  const links = LINKS.map((l, i) => ({ ...l, label: nav.links?.[i]?.label || "" }));
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (e, href) => {
    e.preventDefault();
    setOpen(false);
    scrollToSection(href);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-[rgba(10,10,12,0.82)] backdrop-blur-xl border-b border-[rgba(212,175,55,0.18)]" : "bg-transparent"
      }`}
      data-testid="main-navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-[72px] flex items-center justify-between">
        <a
          href="#top"
          onClick={(e) => go(e, "#hero")}
          className="flex items-center gap-3 group"
          data-testid="nav-brand-link"
        >
          <span className="w-9 h-9 border border-[rgba(212,175,55,0.5)] flex items-center justify-center font-serif-lux text-gold text-lg group-hover:bg-[rgba(212,175,55,0.1)] transition-colors duration-300">
            {(nav.brand || "S").charAt(0)}
          </span>
          <span className="leading-tight">
            <span className="block font-serif-lux text-lg tracking-wide text-[#F7F7F8]">{nav.brand}</span>
            <span className="block font-mono-lux text-[9px] tracking-[0.3em] text-gold">{nav.tag}</span>
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-8" data-testid="nav-links-desktop">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={(e) => go(e, l.href)}
              data-testid={l.testId}
              className="text-[13px] font-sans-lux tracking-wide text-[#B8B8C2] hover:text-gold-champagne transition-colors duration-300"
            >
              {l.label}
            </a>
          ))}
          <a
            href="#contact"
            onClick={(e) => go(e, "#contact")}
            data-testid="nav-cta-button"
            className="btn-shimmer text-[13px] font-semibold tracking-wide text-[#0A0A0B] bg-gold px-5 py-2.5 hover:bg-gold-champagne transition-colors duration-300"
          >
            {nav.cta}
          </a>
        </nav>

        <button
          className="lg:hidden text-gold p-2"
          onClick={() => setOpen(!open)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden bg-[rgba(10,10,12,0.96)] backdrop-blur-xl border-b border-[rgba(212,175,55,0.18)]"
            data-testid="nav-links-mobile"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {links.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={(e) => go(e, l.href)}
                  data-testid={`${l.testId}-mobile`}
                  className="text-sm text-[#B8B8C2] hover:text-gold-champagne transition-colors"
                >
                  {l.label}
                </a>
              ))}
              <a
                href="#contact"
                onClick={(e) => go(e, "#contact")}
                data-testid="nav-cta-button-mobile"
                className="text-sm font-semibold text-[#0A0A0B] bg-gold px-5 py-3 text-center"
              >
                {nav.cta}
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
